import { useCallback, useEffect, useRef, useState } from 'react'
import type { GameViewProps } from '../../core/contracts'
import { haptics } from '../../platform/telegram'
import { useT } from '../../platform/i18n/useT'
import type { TKey } from '../../platform/i18n/i18nContext'
import { COLOR_HEX } from './logic'
import type { ColorId, StroopAnswer, StroopRound } from './logic'
import './view.css'

/**
 * Экран Stroop.
 *
 * Проходит блок проб по одной: слово -> ответ -> следующая.
 * Не успел за лимит - проба засчитывается как пропуск, идём дальше.
 * Накопленные ответы отдаются движку одним пакетом в конце блока.
 */

export function StroopView({ round, level, onAnswer }: GameViewProps<StroopRound, StroopAnswer>) {
  const { t } = useT()
  const [index, setIndex] = useState(0)
  const [flash, setFlash] = useState<'hit' | 'miss' | null>(null)

  const picks = useRef<(ColorId | null)[]>([])
  const times = useRef<number[]>([])
  const shownAt = useRef(performance.now())
  const locked = useRef(false)

  const trial = round.trials[index]

  const finish = useCallback(() => {
    const correctTimes = times.current
    const rtMs =
      correctTimes.length > 0
        ? Math.round(correctTimes.reduce((a, b) => a + b, 0) / correctTimes.length)
        : round.timeLimitMs
    onAnswer({ picks: picks.current, rtMs })
  }, [onAnswer, round.timeLimitMs])

  const submit = useCallback(
    (pick: ColorId | null) => {
      if (locked.current || !trial) return
      locked.current = true

      const rt = performance.now() - shownAt.current
      const hit = pick === trial.ink

      picks.current.push(pick)
      // время меряем только по верным ответам: время ошибки
      // не отражает скорость обработки
      if (hit) times.current.push(rt)

      if (pick === null) haptics.wrong()
      else if (hit) haptics.correct()
      else haptics.wrong()

      setFlash(hit ? 'hit' : 'miss')

      setTimeout(() => {
        setFlash(null)
        locked.current = false
        if (index + 1 >= round.trials.length) {
          finish()
        } else {
          shownAt.current = performance.now()
          setIndex((i) => i + 1)
        }
      }, 220)
    },
    [trial, index, round.trials.length, finish],
  )

  // лимит на пробу
  useEffect(() => {
    shownAt.current = performance.now()
    const id = setTimeout(() => submit(null), round.timeLimitMs)
    return () => clearTimeout(id)
  }, [index, round.timeLimitMs, submit])

  if (!trial) return null

  return (
    <div className="st">
      <div className="st__top">
        <span className="st__level">{t('game.level', { n: level })}</span>
        <span className="st__count">
          {index + 1} / {round.trials.length}
        </span>
      </div>

      <div className="st__timer" aria-hidden="true">
        <div
          key={index}
          className="st__timer-fill"
          style={{ animationDuration: `${round.timeLimitMs}ms` }}
        />
      </div>

      <p className="st__hint">{t('game.stroop.hint')}</p>

      <div className={flash ? `st__stage st__stage--${flash}` : 'st__stage'}>
        <span className="st__word" style={{ color: COLOR_HEX[trial.ink] }}>
          {t(`color.${trial.word}` as TKey)}
        </span>
      </div>

      <div className="st__options">
        {trial.options.map((c) => (
          <button
            key={c}
            type="button"
            className="st__opt"
            style={{ background: COLOR_HEX[c] }}
            onClick={() => submit(c)}
            aria-label={t(`color.${c}` as TKey)}
          />
        ))}
      </div>
    </div>
  )
}
